import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fetchQueues, fetchMetricsHistory } from '../lib/api';
import { QueueCard } from '../components/QueueCard';
import { ThroughputChart } from '../components/ThroughputChart';
import { FailureRateChart } from '../components/FailureRateChart';
import { PageBody, PageHead } from '../components/Page';
import { StatusBanner } from '../components/StatusBanner';
import { TONE_TEXT, type Tone } from '../components/Pill';
import { dateFnsLocale, formatCount, formatRate } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';

function HealthBanner({
  healthy,
  issues,
}: {
  healthy: boolean;
  issues: string[];
}) {
  const { t, tCount } = useLocale();

  if (healthy) {
    return <StatusBanner tone="success">{t('overview.healthy')}</StatusBanner>;
  }
  return (
    <StatusBanner tone="error">
      {/* The issues themselves are the API's own wording, rendered as received — translating a
          server-side diagnosis belongs to the API's `i18nServer`, not to this catalogue. */}
      <p className="font-medium">{tCount('overview.issues', issues.length)}</p>
      <ul className="mt-2 space-y-1 pl-5 list-disc">
        {issues.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </StatusBanner>
  );
}

/** The four cross-queue totals, shown in the page head next to the title. */
function Totals({
  totals,
}: {
  readonly totals: Record<'waiting' | 'active' | 'delayed' | 'failed', number>;
}) {
  const { t, locale } = useLocale();

  const items: { label: string; value: number; tone: Tone }[] = [
    { label: t('queueStats.waiting'), value: totals.waiting, tone: 'sky' },
    { label: t('queueStats.active'), value: totals.active, tone: 'green' },
    { label: t('queueStats.delayed'), value: totals.delayed, tone: 'amber' },
    {
      label: t('queueStats.failed'),
      value: totals.failed,
      tone: totals.failed > 0 ? 'red' : 'gray',
    },
  ];

  return (
    <div className="flex flex-wrap gap-6">
      {items.map(({ label, value, tone }) => (
        <div key={label} className="text-center min-w-[4rem]">
          <p className={`text-xl font-bold tabular-nums ${TONE_TEXT[tone]}`}>
            {formatCount(value, locale)}
          </p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      ))}
    </div>
  );
}

export function OverviewPage() {
  const { t, locale } = useLocale();
  const navigate = useNavigate();

  const {
    data: queuesData,
    isLoading: queuesLoading,
    error: queuesError,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['queues'],
    queryFn: fetchQueues,
    refetchInterval: 5_000,
  });

  const { data: metricsData } = useQuery({
    queryKey: ['metrics', 60],
    queryFn: () => fetchMetricsHistory(60),
    refetchInterval: 60_000,
  });

  const history = metricsData?.history ?? [];

  const totals = queuesData
    ? {
        waiting: queuesData.queues.reduce((s, q) => s + q.waiting, 0),
        active: queuesData.queues.reduce((s, q) => s + q.active, 0),
        failed: queuesData.queues.reduce((s, q) => s + q.failed, 0),
        delayed: queuesData.queues.reduce((s, q) => s + q.delayed, 0),
      }
    : null;

  return (
    <>
      <PageHead
        eyebrow={t('common.operations')}
        title={t('overview.title')}
        lead={
          dataUpdatedAt
            ? t('overview.updated', {
                time: format(new Date(dataUpdatedAt), 'HH:mm:ss', {
                  locale: dateFnsLocale(locale),
                }),
              })
            : undefined
        }
        actions={totals ? <Totals totals={totals} /> : undefined}
      />

      <PageBody>
        {queuesData && (
          <HealthBanner
            healthy={queuesData.health.healthy}
            issues={queuesData.health.issues}
          />
        )}

        {queuesError && (
          <StatusBanner tone="error">
            {t('overview.fetchFailed', {
              message: (queuesError as Error).message,
            })}
          </StatusBanner>
        )}

        {/* Queue cards */}
        {queuesLoading && !queuesData ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-40 bg-white rounded-lg border border-gray-200 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {queuesData?.queues.map((q) => (
              <QueueCard
                key={q.name}
                queue={q}
                onClick={() => navigate(`/queue/${q.name}`)}
              />
            ))}
          </div>
        )}

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="surface p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              {t('overview.throughputTitle')}
            </h2>
            <ThroughputChart history={history} />
          </div>

          <div className="surface p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              {t('overview.failureTitle')}
            </h2>
            <FailureRateChart history={history} />
          </div>
        </div>

        {/* Capacity table — current snapshot */}
        {queuesData && (
          <div className="surface p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              {t('overview.capacityTitle')}
            </h2>
            <div className="overflow-x-auto">
              <table className="data-table min-w-[600px]">
                <thead>
                  <tr>
                    <th className="pb-3 pr-6">{t('overview.columnQueue')}</th>
                    <th className="pb-3 pr-6 text-right">
                      {t('queueStats.waiting')}
                    </th>
                    <th className="pb-3 pr-6 text-right">
                      {t('queueStats.active')}
                    </th>
                    <th className="pb-3 pr-6 text-right">
                      {t('queueStats.delayed')}
                    </th>
                    <th className="pb-3 pr-6 text-right">
                      {t('queueStats.completed')}
                    </th>
                    <th className="pb-3 pr-6 text-right">
                      {t('queueStats.failed')}
                    </th>
                    <th className="pb-3 pr-6 text-right">
                      {t('overview.columnRate')}
                    </th>
                    <th className="pb-3 text-right">
                      {t('overview.columnFailPercent')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {queuesData.queues.map((q) => (
                    <tr
                      key={q.name}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => navigate(`/queue/${q.name}`)}
                    >
                      <td className="py-3 pr-6 font-medium text-gray-900">
                        {q.name}
                        {q.paused && (
                          <span className="ml-2 text-xs text-amber-600">
                            {t('overview.pausedSuffix')}
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-6 text-right text-sky-600 tabular-nums">
                        {formatCount(q.waiting, locale)}
                      </td>
                      <td className="py-3 pr-6 text-right text-emerald-600 tabular-nums">
                        {formatCount(q.active, locale)}
                      </td>
                      <td className="py-3 pr-6 text-right text-amber-600 tabular-nums">
                        {formatCount(q.delayed, locale)}
                      </td>
                      <td className="py-3 pr-6 text-right text-gray-600 tabular-nums">
                        {formatCount(q.completed, locale)}
                      </td>
                      <td className="py-3 pr-6 text-right tabular-nums">
                        <span
                          className={
                            q.failed > 0
                              ? 'text-red-600 font-semibold'
                              : 'text-gray-500'
                          }
                        >
                          {formatCount(q.failed, locale)}
                        </span>
                      </td>
                      <td className="py-3 pr-6 text-right text-gray-600 tabular-nums">
                        {formatRate(q.processingRate, locale)}
                      </td>
                      <td className="py-3 text-right tabular-nums">
                        <span
                          className={
                            q.failureRate > 10
                              ? 'text-red-600 font-semibold'
                              : 'text-gray-600'
                          }
                        >
                          {t('common.percentValue', {
                            rate: formatRate(q.failureRate, locale),
                          })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
